import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import Parser from 'html-react-parser';
import { withTheme } from 'styled-components';
import { getAttributeFromHtml } from 'utils/element';
import { ContentTypes } from 'constants/contentTypes';
import { POPUP_ID_IMAGE_PREVIEW } from 'constants/popups';
import * as actions from 'store/popup/actions';
import { localize } from 'core/localization';
import ContentWrapper from '../ContentWrapper';
import Document from './Document';
import HotspotOnImage from './HotspotOnImage';
import ShowMore from './ShowMore';
import LinkCuration from './LinkCuration';
import Checklist from './Checklist';
import ChecklistItem from './Checklist/checklistItem';
import HowTo from './HowTo';
import PreviewGallery from '../../popups/previewGallery/PreviewGallery';
import AudioWrapper from './Audio/Audio.styled';
import VideoWrapper from './Video/Video.styled';
import { Container } from './ContentItem.styled';
import {
  ZoomIcon,
  ZoomIconBg,
  ZoomIconButton
} from '../../common/MultipleChoiceItems/MultipleChoiceItems.styled';
import {
  IMAGE_SRC_KEY,
  IMAGE_ZOOM_DIV_ID,
  IMAGE_ZOOM_DIV_ID_OLD_EDITOR
} from '../ContentWrapper/image';

type ContentItemProps = {
  value: string;
  childrenElements: any[];
  inverted: boolean;
  theme: { [key: string]: any };
  itemId: string;
  linkTabIndex?: number;
  popupActions: { [key: string]: any };
};

export const ContentItem = ({
  value,
  inverted,
  theme,
  childrenElements,
  itemId,
  linkTabIndex,
  popupActions
}: ContentItemProps) => (
  <Container inverted={inverted}>
    {renderContent(value, childrenElements, theme, inverted, itemId, linkTabIndex, popupActions)}
  </Container>
);

const showPopup = (imageUrl: string, popupActions: { [key: string]: any }) => {
  popupActions.openPopup({
    popupId: POPUP_ID_IMAGE_PREVIEW,
    popupAriaLabelKey: '[aria label gallery popup]',
    component: PreviewGallery,
    imageUrls: [imageUrl],
    disableBottomLine: true
  });
};

const addZoomButton = (
  imgURL: string,
  popupActions: { [key: string]: any },
  isOldEditor = false
) => (
  <ZoomIconButton
    className={isOldEditor ? 'zoom-button-old-editor' : 'zoom-button'}
    ariaLabel={localize('[aria label zoom button]')}
    onClick={() => showPopup(imgURL, popupActions)}
    tabIndex={0}
    isShown={true}
  >
    <ZoomIconBg size={41} name="zoom-bg" />
    <ZoomIcon size={41} name="zoom" />
  </ZoomIconButton>
);

function renderContent(
  contentHtml: any,
  childrenElements: any[],
  theme: { [key: string]: any },
  inverted: boolean,
  itemId: string,
  linkTabIndex: any,
  popupActions: { [key: string]: any }
) {
  const type = getAttributeFromHtml(contentHtml, 'data-type');
  switch (type) {
    case ContentTypes.Document:
      return <Document content={contentHtml} inverted={inverted} />;
    case ContentTypes.Hotspot:
      return <HotspotOnImage content={contentHtml} />;
    case ContentTypes.ExpandableBlock:
      return (
        <ShowMore
          content={contentHtml}
          childrenElements={childrenElements}
          inverted={inverted}
          linkTabIndex={linkTabIndex}
        />
      );
    case ContentTypes.LinkCuration:
      return <LinkCuration content={contentHtml} />;
    case ContentTypes.SingleAudio:
      return <AudioWrapper dangerouslySetInnerHTML={ContentWrapper.wrap(contentHtml, theme)} />;
    case ContentTypes.VideoInTheLeft:
    case ContentTypes.VideoWithText:
    case ContentTypes.VideoInTheRight:
    case ContentTypes.SingleVideo:
      return <VideoWrapper dangerouslySetInnerHTML={ContentWrapper.wrap(contentHtml, theme)} />;
    case ContentTypes.Checklist:
      return (
        <Checklist
          content={contentHtml}
          childrenElements={childrenElements}
          inverted={inverted}
          linkTabIndex={linkTabIndex}
        />
      );
    case ContentTypes.ChecklistItem:
      return (
        <ChecklistItem
          itemId={itemId}
          content={contentHtml}
          childrenElements={childrenElements}
          inverted={inverted}
          linkTabIndex={linkTabIndex}
        />
      );
    case ContentTypes.HowTo:
      return (
        <HowTo
          content={contentHtml}
          childrenElements={childrenElements}
          inverted={inverted}
          linkTabIndex={linkTabIndex}
        />
      );
    default:
      return (
        <div data-test="content-default">
          {Parser(ContentWrapper.wrap(contentHtml, theme, linkTabIndex).__html, {
            replace: (domNode: any) => {
              if (domNode.attribs) {
                const imgURL = domNode.attribs[IMAGE_SRC_KEY];

                if (domNode.attribs.id === IMAGE_ZOOM_DIV_ID) {
                  return addZoomButton(imgURL, popupActions);
                }

                if (domNode.attribs.id === IMAGE_ZOOM_DIV_ID_OLD_EDITOR) {
                  return addZoomButton(imgURL, popupActions, true);
                }
              }

              return domNode;
            }
          })}
        </div>
      );
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return { popupActions: bindActionCreators(actions, dispatch) };
}

export default withTheme(connect(null, mapDispatchToProps)(ContentItem));
